import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminProvidersApi } from "@/api/oraclePhase28Client";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — AdminOne Provider Status Panel (Phase 29E).
 *
 * Unified board showing the readiness + last-known state of every external
 * provider used across the stack:
 *   • Twilio (SMS), SendGrid + SMTP (Email), ElevenLabs, OpenAI Whisper / Vision,
 *     FCM / APNs / OneSignal (push), Slack / Discord / Generic webhooks.
 *
 * Status pills:
 *   • Awaiting Provider Key  — env vars missing
 *   • Configured             — env vars present, no recent send yet
 *   • Failed                 — env vars present, last send failed
 *   • Live                   — env vars present, last send succeeded
 *
 * Additive only — does not remove or alter any existing AdminOne panel.
 */

const STATUS_COLORS = {
  awaiting_key: "#fcd34d",
  configured:   "#22d3ee",
  failed:       "#ef4444",
  live:         "#10b981",
};

const STATUS_LABELS = {
  awaiting_key: "Awaiting Provider Key",
  configured:   "Configured",
  failed:       "Failed",
  live:         "Live",
};

const CATEGORY_LABELS = {
  messaging: "Messaging · SMS / Email",
  voice:     "Voice · Speech · Vision",
  push:      "Mobile · Smartwatch · TV Push",
  webhook:   "Webhooks · Slack · Discord · Custom",
};

const CATEGORY_ORDER = ["messaging", "voice", "push", "webhook"];

function StatusPill({ status, label, testid }) {
  const color = STATUS_COLORS[status] || "#94a3b8";
  return (
    <span
      style={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 10.5,
        letterSpacing: "0.08em",
        fontWeight: 800,
        padding: "3px 10px",
        border: `1px solid ${color}`,
        borderRadius: 999,
        color,
        background: `${color}1a`,
        whiteSpace: "nowrap",
      }}
      data-testid={testid}
    >
      {(label || STATUS_LABELS[status] || status || "—").toUpperCase()}
    </span>
  );
}

function ProviderRow({ p, testid }) {
  const last = p.last_event || {};
  return (
    <article style={S.row} data-testid={testid}>
      <div style={S.rowHead}>
        <span style={S.rowIcon} aria-hidden="true">{p.icon || "•"}</span>
        <div style={S.rowMeta}>
          <strong style={S.rowLabel}>{p.label}</strong>
          <code style={S.rowKeys}>
            {(p.env_keys?.length ? p.env_keys.join(" · ") : (p.env_keys_any || []).map((k) => `${k} (any)`).join(" · ")) || "(no key required)"}
          </code>
        </div>
        <StatusPill status={p.status} label={p.status_label} testid={`${testid}-pill`} />
      </div>
      {(last && last.status) && (
        <div style={S.rowLast}>
          <span style={S.rowLastLbl}>Last event</span>
          <code style={S.rowLastVal}>{(last.timestamp || "").slice(0, 19).replace("T", " ")}</code>
          <span style={S.rowLastLbl}>·</span>
          <span style={{ ...S.rowLastLbl, color: STATUS_COLORS[p.status] || "#94a3b8" }}>
            {(last.status || "").toUpperCase()}
          </span>
          {last.reason && <span style={S.rowLastNote}>· {String(last.reason).slice(0, 120)}</span>}
        </div>
      )}
      {p.docs_url && (
        <a href={p.docs_url} target="_blank" rel="noreferrer" style={S.docsLink} data-testid={`${testid}-docs`}>
          Provider docs ↗
        </a>
      )}
    </article>
  );
}

export default function AdminProviderStatusPanel({ testid = "admin-provider-status-panel" } = {}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data: payload } = await adminProvidersApi.status();
      setData(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "providers_status_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = { messaging: [], voice: [], push: [], webhook: [] };
    (data?.providers || []).forEach((p) => {
      const cat = out[p.category] ? p.category : "messaging";
      out[cat].push(p);
    });
    return out;
  }, [data]);

  const summary = data?.summary || { total: 0, live: 0, configured: 0, failed: 0, awaiting_key: 0 };

  return (
    <section style={S.shell} data-testid={testid}>
      <ComplianceLock variant="banner" policy="v27" testid={`${testid}-compliance-top`} />

      <header style={S.head}>
        <div>
          <span style={S.tagline}>Phase 29E · Provider Status Mesh™</span>
          <h3 style={S.title}>External Provider Readiness</h3>
        </div>
        <div style={S.headRight}>
          <span style={S.summaryChip} data-testid={`${testid}-summary`}>
            {summary.live} live · {summary.configured} configured · {summary.failed} failed · {summary.awaiting_key} awaiting
          </span>
          <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid={`${testid}-refresh`}>
            {busy ? "…" : "↻ Refresh"}
          </button>
        </div>
      </header>

      {err && <div style={S.err} data-testid={`${testid}-error`}>⚠ {err}</div>}

      <div style={S.legend}>
        <StatusPill status="live" />
        <StatusPill status="configured" />
        <StatusPill status="failed" />
        <StatusPill status="awaiting_key" />
        <span style={S.legendNote}>
          Status is computed from <code style={S.legendCode}>.env</code> presence + the most recent dispatch result in MongoDB.
          Add provider keys to <code style={S.legendCode}>/app/backend/.env</code> to flip Awaiting → Configured → Live.
        </span>
      </div>

      <div style={S.groupGrid}>
        {CATEGORY_ORDER.map((cat) => {
          const rows = grouped[cat] || [];
          if (rows.length === 0) return null;
          return (
            <section key={cat} style={S.group} data-testid={`${testid}-group-${cat}`}>
              <header style={S.groupHead}>
                <strong style={S.groupTitle}>{CATEGORY_LABELS[cat] || cat}</strong>
                <span style={S.groupCount}>{rows.length}</span>
              </header>
              <div style={S.groupBody}>
                {rows.map((p) => (
                  <ProviderRow key={p.id} p={p} testid={`provider-row-${p.id}`} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance-footer`} />
    </section>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.92))", border: "1px solid rgba(197,160,89,0.30)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  headRight: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 16 },
  summaryChip: { color: "#c5a059", border: "1px solid rgba(197,160,89,0.45)", padding: "4px 10px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.08em", fontWeight: 800, background: "rgba(197,160,89,0.10)" },
  refresh: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "5px 11px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  legend: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "8px 10px", background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10 },
  legendNote: { color: "#94a3b8", fontSize: 11, lineHeight: 1.4, flex: 1, minWidth: 200 },
  legendCode: { background: "rgba(255,255,255,0.05)", padding: "1px 6px", borderRadius: 4, fontFamily: "'Space Mono', monospace", color: "#cbd5e1" },
  groupGrid: { display: "flex", flexDirection: "column", gap: 10 },
  group: { background: "rgba(0,0,0,0.18)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8 },
  groupHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  groupTitle: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.12em" },
  groupCount: { color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 10.5, padding: "2px 7px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6 },
  groupBody: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 8 },
  row: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  rowHead: { display: "flex", alignItems: "flex-start", gap: 10 },
  rowIcon: { fontSize: 18, lineHeight: 1.2 },
  rowMeta: { display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 },
  rowLabel: { color: "#fde68a", fontSize: 13, lineHeight: 1.25, wordBreak: "break-word" },
  rowKeys: { color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.04em", wordBreak: "break-word" },
  rowLast: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  rowLastLbl: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  rowLastVal: { fontFamily: "'Space Mono', monospace", color: "#cbd5e1", fontSize: 10.5, background: "rgba(255,255,255,0.04)", padding: "2px 6px", borderRadius: 6 },
  rowLastNote: { color: "#94a3b8", fontSize: 10.5, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis" },
  docsLink: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, textDecoration: "none", borderBottom: "1px dotted #c5a059", alignSelf: "flex-start" },
};
